import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";

// A single, symmetric maple leaf drawn on a 100 × 100 grid. Used as a brand
// accent inside buttons, as list markers and as quiet section watermarks.
export const MAPLE_LEAF_PATH = "M50 3 L56.5 17.5 L64.5 13.5 L61.5 34 L74 23.5 L78.5 32 L90.5 28.5 L86 42 L95 46 L72.5 62 L76.5 70.5 L54 66.5 L54 93 L46 93 L46 66.5 L23.5 70.5 L27.5 62 L5 46 L14 42 L9.5 28.5 L21.5 32 L26 23.5 L38.5 34 L35.5 13.5 L43.5 17.5 Z";
type MapleLeafProps = {
  size?: number;
  className?: string;
  title?: string;
};

/** A solid maple leaf that inherits `currentColor`. Decorative unless a title is given. */
export function MapleLeaf({
  size = 14,
  className = "",
  title
}: MapleLeafProps) {
  return <svg width={size} height={size} viewBox="0 0 100 100" className={className} fill="currentColor" role={title ? "img" : undefined} aria-hidden={title ? undefined : true} aria-label={title} data-id="element-296" data-mp="4h0gi8b1wb63">
      <path d={MAPLE_LEAF_PATH} data-id="element-297" data-mp="rtxoh5jw4z3r" />
    </svg>;
}
type MapleWatermarkProps = {
  className?: string;
  /** Tailwind text colour + opacity for the leaf, e.g. "text-white/[0.05]". */
  tone?: string;
  rotate?: number;
};

/**
 * An oversized leaf that drifts and turns a few degrees as the section scrolls.
 * Place inside a `relative overflow-hidden` section.
 */
export function MapleWatermark({
  className = "-right-24 -top-16 h-[34rem] w-[34rem]",
  tone = "text-white/[0.05]",
  rotate = -12
}: MapleWatermarkProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const r = useTransform(scrollYProgress, [0, 1], reduce ? [rotate, rotate] : [rotate - 8, rotate + 8]);
  const y = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [40, -40]);
  return <motion.div ref={ref} aria-hidden="true" style={{
    rotate: r,
    y
  }} className={`pointer-events-none absolute ${tone} ${className}`} data-id="element-298" data-mp="1pg4os395w5ab">
      <svg viewBox="0 0 100 100" className="h-full w-full" fill="currentColor" data-id="element-299" data-mp="28f84ld9jd6iz">
        <path d={MAPLE_LEAF_PATH} data-id="element-300" data-mp="2dbq49yq0eeog" />
      </svg>
    </motion.div>;
}